import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../ui/Button';
import {
  primaryNavigationItems,
  type NavigationItem,
} from '../../../app/routes/navigation';

interface SidebarNavProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

const SidebarLink = ({ item, collapsed = false }: { item: NavigationItem; collapsed?: boolean }) => {
  const location = useLocation();
  const isActive = item.path === '/'
    ? location.pathname === '/'
    : item.matchStartsWith
      ? location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)
      : location.pathname === item.path;

  return (
    <NavLink
      to={item.path}
      className={cn(
        'group flex items-center rounded-2xl border px-3 py-3 transition-all',
        collapsed ? 'justify-center' : 'justify-between',
        isActive
          ? 'border-[#39a74b] bg-[#39a74b] text-white shadow-sm'
          : 'border-transparent text-white/78 hover:border-white/10 hover:bg-white/10 hover:text-white'
      )}
      aria-label={collapsed ? item.label : undefined}
      title={collapsed ? item.label : undefined}
    >
      <div className={cn('flex items-center', collapsed ? 'justify-center' : 'gap-3')}>
        <div
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-2xl transition-colors',
            isActive
              ? 'bg-white text-[#39a74b]'
              : 'bg-white/10 text-white/88 group-hover:bg-white/14 group-hover:text-white'
          )}
        >
          <item.icon size={18} />
        </div>
        {!collapsed ? <span className="text-sm font-semibold">{item.label}</span> : null}
      </div>
      {!collapsed ? (
        <ChevronRight
          size={16}
          className={cn('transition-colors', isActive ? 'text-white/60' : 'text-white/35 group-hover:text-white/60')}
        />
      ) : null}
    </NavLink>
  );
};

const SidebarNav = ({ collapsed = false, onToggle }: SidebarNavProps) => {
  return (
    <div className={cn('hidden shrink-0 transition-[width] duration-300 lg:block', collapsed ? 'w-[104px]' : 'w-[280px]')}>
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex flex-col border-r border-primary-dark/30 bg-primary px-4 py-6 text-white shadow-2xl transition-[width] duration-300',
          collapsed ? 'w-[104px]' : 'w-[280px]'
        )}
      >
        <div className={cn('flex items-center px-2', collapsed ? 'justify-center' : 'justify-between gap-3')}>
          <div className={cn('flex items-center', collapsed ? 'justify-center' : 'gap-3')}>
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/95 shadow-sm">
              <img src="/logo.png" alt="Logo IEADJO" className="h-full w-full rounded-2xl object-cover" />
            </div>
            {!collapsed ? (
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold uppercase tracking-[0.2em] text-white/70">IEADJO</p>
                <h1 className="truncate text-lg font-bold text-white">Gestão EBD</h1>
              </div>
            ) : null}
          </div>

          {!collapsed ? (
            <button
              type="button"
              onClick={onToggle}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-white/12 bg-white/10 text-white transition hover:bg-white/16"
              aria-label="Recolher menu lateral"
              title="Recolher menu lateral"
            >
              <ChevronLeft size={18} />
            </button>
          ) : null}
        </div>

        {collapsed ? (
          <div className="mt-5 flex justify-center">
            <button
              type="button"
              onClick={onToggle}
              className="flex h-10 w-10 items-center justify-center rounded-2xl border border-white/12 bg-white/10 text-white transition hover:bg-white/16"
              aria-label="Expandir menu lateral"
              title="Expandir menu lateral"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        ) : null}

        <div className="mt-8 space-y-2">
          {!collapsed ? (
            <p className="px-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-white/50">Principal</p>
          ) : null}
          <div className="space-y-1.5">
            {primaryNavigationItems.map((item) => (
              <SidebarLink key={item.path} item={item} collapsed={collapsed} />
            ))}
          </div>
        </div>
      </aside>
    </div>
  );
};

export default SidebarNav;
